import type { Classification } from '@heivol-finance/calc';
import { parseKrToOre } from './format.js';

/**
 * CSV import parsing for the Settings → "Importér" card (net-worth one-off events).
 * Pure parsing only — no DOM, no API calls. Columns: date,label,amount,type
 * (type optional; English keywords plus Danish aliases).
 */

export interface ParsedRow {
  line: number;
  dated: string;
  label: string;
  amount_ore: number;
  classification: Classification;
  error: string | null;
}

export const TYPE_MAP: Record<string, Classification> = {
  // English (documented format)
  consumptive: 'consumptive',
  asset: 'asset-converting',
  debt: 'debt-service',
  // Danish keywords accepted as aliases
  forbrug: 'consumptive',
  aktiv: 'asset-converting',
  afdrag: 'debt-service',
};

/** Split a CSV line on commas, tolerating simple double-quoted fields. */
export function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i] ?? '';
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

export const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Parse CSV text into preview rows. Columns: date,label,amount,type (type optional). */
export function parseCsv(text: string): ParsedRow[] {
  const rows: ParsedRow[] = [];
  const lines = text.split(/\r?\n/);
  let seenData = false;
  for (let i = 0; i < lines.length; i++) {
    const raw = (lines[i] ?? '').trim();
    if (!raw) continue;
    const cols = splitCsvLine(raw);
    const date = (cols[0] ?? '').trim();
    const label = (cols[1] ?? '').trim();
    const amount = (cols[2] ?? '').trim();
    const type = (cols[3] ?? '').trim().toLowerCase();

    // Tolerate a header row (first data-bearing line whose first cell isn't a date).
    if (!seenData && !DATE_RE.test(date)) {
      const lower = date.toLowerCase();
      if (lower === 'date' || lower.startsWith('date') || lower === 'dato' || lower.startsWith('dato')) continue;
    }

    const lineNo = i + 1;
    let error: string | null = null;
    if (!DATE_RE.test(date)) error = 'Invalid date (expected YYYY-MM-DD)';
    const amount_ore = parseKrToOre(amount);
    if (!error && amount_ore === 0 && !/[0-9]/.test(amount)) error = 'Invalid amount';

    if (!error) seenData = true;
    const classification: Classification = TYPE_MAP[type] ?? 'consumptive';

    rows.push({
      line: lineNo,
      dated: date,
      label: label || 'Imported entry',
      amount_ore,
      classification,
      error,
    });
  }
  return rows;
}
